import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Clock, Filter, Upload, FileDown, FileSpreadsheet } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { TimeEntryForm } from './components/TimeEntryForm';
import { TimeEntryCard } from './components/TimeEntryCard';
import { TimeTimer } from './components/TimeTimer';
import { TimeEntrySummary } from './components/TimeEntrySummary';
import { TimeEntryImportDialog } from './components/TimeEntryImportDialog';
import { useTimeEntries } from '../../core/hooks/useTimeEntries';
import { useClients } from '../../core/hooks/useClients';
import { useUsers } from '../../core/hooks/useUsers';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { useAuth } from '../../core/contexts/AuthContext';
import type { TimeEntry } from '../../core/models/TimeEntry';
import { TimeEntryStatus, TIME_ENTRY_TYPES } from '../../core/models/TimeEntry';
import { UserRole } from '../../core/models/User';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';

/** HTML date inputs use `yyyy-MM-dd`; parsing with `Date` alone is UTC-based and skips most of the end day locally. */
function parseLocalDateFromInput(yyyyMmDd: string): Date {
  const parts = yyyyMmDd.split('-').map((v) => Number.parseInt(v, 10));
  const [y, m, d] = parts;
  if (parts.length !== 3 || [y, m, d].some((n) => Number.isNaN(n))) {
    return new Date(yyyyMmDd);
  }
  return new Date(y, m - 1, d);
}
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { exportTimeEntriesCsv, exportTimeEntriesPdf } from './utils/timeEntryExport';

export const TimeTrackingPage: React.FC = () => {
  const { organizationId, clientId, currentUser } = useAuth();
  const { organizations } = useOrganizations();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;

  /** Parent client scope from profile. */
  const profileScopeId = organizationId || clientId || null;

  /** Super admin: optional filter to one parent client; empty = all tenants. */
  const [filterParentClientId, setFilterParentClientId] = useState<string>('');
  const [filterViewerUserId, setFilterViewerUserId] = useState<string>('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'custom' | 'all'>('all');
  const [customStart, setCustomStart] = useState(format(startOfWeek(new Date()), 'yyyy-MM-dd'));
  const [customEnd, setCustomEnd] = useState(format(endOfWeek(new Date()), 'yyyy-MM-dd'));
  const [filterBillable, setFilterBillable] = useState<boolean | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<TimeEntryStatus | 'all'>('all');
  /** Project filter: null = all, `'org'` = client-level only, else project id. */
  const [filterProjectId, setFilterProjectId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string | 'all'>('all');
  const [importOpen, setImportOpen] = useState(false);

  const effectiveQueryClientId =
    isSuperAdmin && filterParentClientId ? filterParentClientId : isSuperAdmin ? undefined : profileScopeId ?? undefined;

  const scopeForClients = isSuperAdmin ? (filterParentClientId || null) : profileScopeId;

  const userListOrgId = isSuperAdmin
    ? filterParentClientId || undefined
    : profileScopeId || undefined;

  const { users: usersForFilter } = useUsers(userListOrgId);

  /** Must be memoized: a fresh object each render would resubscribe `useTimeEntries` in a loop. */
  const range = useMemo(() => {
    switch (dateRange) {
      case 'week':
        return {
          start: startOfWeek(new Date()),
          end: endOfWeek(new Date()),
        };
      case 'month':
        return {
          start: startOfMonth(new Date()),
          end: endOfMonth(new Date()),
        };
      case 'custom':
        return {
          start: startOfDay(parseLocalDateFromInput(customStart)),
          end: endOfDay(parseLocalDateFromInput(customEnd)),
        };
      default:
        return undefined;
    }
  }, [dateRange, customStart, customEnd]);

  const { clients } = useClients(scopeForClients);

  const viewerUserIdParam = filterViewerUserId || undefined;

  const timeEntriesOptions = {
    clientId: effectiveQueryClientId,
    viewerUserId: viewerUserIdParam,
    projectId: filterProjectId === 'org' ? 'none' : filterProjectId,
    type: filterType !== 'all' ? filterType : undefined,
    dateRange: range,
    billable: filterBillable,
    status: filterStatus !== 'all' ? filterStatus : undefined,
  };

  const { timeEntries, loading, error, createTimeEntry, updateTimeEntry, deleteTimeEntry, rejectTimeEntry } =
    useTimeEntries(timeEntriesOptions);

  /** Refetching on a filter change should not unmount the filters or a running timer. */
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  const { timeEntries: existingEntriesForImport } = useTimeEntries({
    clientId: effectiveQueryClientId,
    projectId: filterProjectId === 'org' ? 'none' : filterProjectId,
    type: filterType !== 'all' ? filterType : undefined,
    dateRange: range,
    billable: filterBillable,
    status: filterStatus !== 'all' ? filterStatus : undefined,
  });

  const userIdToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of usersForFilter) {
      const label = u.displayName?.trim() || u.email || u.uid;
      map[u.uid] = label;
    }
    if (currentUser?.uid) {
      map[currentUser.uid] =
        currentUser.displayName?.trim() || currentUser.email || currentUser.uid;
    }
    return map;
  }, [usersForFilter, currentUser?.uid, currentUser?.displayName, currentUser?.email]);

  const projectIdToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    clients.forEach((client) => {
      map[client.id] = client.name;
    });
    return map;
  }, [clients]);

  const exportClientName = useMemo(() => {
    if (isSuperAdmin && filterParentClientId) {
      return organizations.find((org) => org.id === filterParentClientId)?.name || filterParentClientId;
    }
    return undefined;
  }, [filterParentClientId, isSuperAdmin, organizations]);

  const exportProjectName = useMemo(() => {
    if (!filterProjectId) return undefined;
    if (filterProjectId === 'org') return 'Client only';
    return projectIdToLabel[filterProjectId] || filterProjectId;
  }, [filterProjectId, projectIdToLabel]);

  const dateRangeLabel = useMemo(() => {
    if (!range) return 'All time';
    return `${format(range.start, 'MMM d, yyyy')} - ${format(range.end, 'MMM d, yyyy')}`;
  }, [range]);

  const exportContext = useMemo(
    () => ({
      clientName: exportClientName,
      projectName: exportProjectName,
      userName: filterViewerUserId ? userIdToLabel[filterViewerUserId] || filterViewerUserId : undefined,
      dateRangeLabel,
      projectNamesById: projectIdToLabel,
      userNamesById: userIdToLabel,
    }),
    [dateRangeLabel, exportClientName, exportProjectName, filterViewerUserId, projectIdToLabel, userIdToLabel]
  );

  const handleExportPdf = async () => {
    try {
      await exportTimeEntriesPdf(timeEntries, exportContext);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export PDF. Please try again.');
    }
  };

  const handleExportCsv = () => {
    try {
      exportTimeEntriesCsv(timeEntries, exportContext);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const allowedImportUserIds = useMemo(() => {
    if (currentUser?.role === UserRole.PROJECT_ADMIN && currentUser.uid) {
      return new Set<string>([currentUser.uid]);
    }
    const s = new Set<string>();
    usersForFilter.forEach((u) => s.add(u.uid));
    if (currentUser?.uid) s.add(currentUser.uid);
    return s;
  }, [usersForFilter, currentUser?.uid, currentUser?.role]);

  const handleTimerStop = async (duration: number, startTime: Date, endTime: Date) => {
    const orgForEntry = isSuperAdmin ? filterParentClientId || profileScopeId || '' : profileScopeId || '';
    setEditingEntry({
      id: '',
      clientId: orgForEntry,
      organizationId: orgForEntry,
      userId: currentUser?.uid || '',
      description: '',
      startTime,
      endTime,
      duration,
      billable: true,
      status: TimeEntryStatus.PENDING,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as TimeEntry);
    setFormOpen(true);
  };

  const handleSubmit = async (data: Omit<TimeEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editingEntry?.id) {
        await updateTimeEntry(editingEntry.id, data);
      } else {
        await createTimeEntry(data);
      }
      setFormOpen(false);
      setEditingEntry(null);
    } catch (err) {
      console.error('Error saving time entry:', err);
      throw err;
    }
  };

  const handleEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setFormOpen(true);
  };

  const handleDelete = async (entry: TimeEntry) => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      try {
        await deleteTimeEntry(entry.id);
      } catch (err) {
        console.error('Error deleting time entry:', err);
        alert('Failed to delete time entry');
      }
    }
  };

  const handleReject = async (entry: TimeEntry) => {
    try {
      await rejectTimeEntry(entry.id);
    } catch (err) {
      console.error('Error rejecting time entry:', err);
      alert('Failed to reject time entry');
    }
  };

  if (loading && !hasLoadedOnce) {
    return <LoadingState message="Loading time entries..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load time entries" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Time Tracking"
        description="Track billable hours and time entries"
        icon={Clock}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import records
            </Button>
            <Button variant="outline" onClick={handleExportPdf} disabled={timeEntries.length === 0}>
              <FileDown className="mr-2 h-4 w-4" />
              PDF
            </Button>
            <Button variant="outline" onClick={handleExportCsv} disabled={timeEntries.length === 0}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              CSV
            </Button>
            <Button
              onClick={() => {
                setEditingEntry(null);
                setFormOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Entry
            </Button>
          </div>
        }
      />

      <TimeTimer onStop={handleTimerStop} />

      <TimeEntrySummary entries={timeEntries} dateRange={range} />

      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <h3 className="font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isSuperAdmin && (
            <div className="space-y-2 md:col-span-2 lg:col-span-2">
              <label className="text-sm font-medium">Parent client (scope)</label>
              <Select
                value={filterParentClientId || '__all__'}
                onValueChange={(v) => {
                  setFilterParentClientId(v === '__all__' ? '' : v);
                  setFilterViewerUserId('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All clients</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">User</label>
            <Select
              value={filterViewerUserId || '__all__'}
              onValueChange={(v) => setFilterViewerUserId(v === '__all__' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All users</SelectItem>
                {usersForFilter.map((u) => (
                  <SelectItem key={u.uid} value={u.uid}>
                    {u.displayName?.trim() || u.email || u.uid}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSuperAdmin && !filterParentClientId && (
              <p className="text-xs text-gray-500">Select a parent client to list users, or filter by all entries.</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Date Range</label>
            <Select value={dateRange} onValueChange={(value) => setDateRange(value as 'week' | 'month' | 'custom' | 'all')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {dateRange === 'custom' && (
            <>
              <Input
                label="Start Date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <Input
                label="End Date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Project</label>
            <Select
              value={filterProjectId ?? 'all'}
              onValueChange={(v) => setFilterProjectId(v === 'all' ? null : v === 'org' ? 'org' : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="org">Client only</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Type</label>
            <Select value={filterType} onValueChange={(v) => setFilterType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TIME_ENTRY_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value as TimeEntryStatus | 'all')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value={TimeEntryStatus.PENDING}>Pending</SelectItem>
                <SelectItem value={TimeEntryStatus.REJECTED}>Rejected</SelectItem>
                <SelectItem value={TimeEntryStatus.BILLED}>Billed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Billable</label>
            <Select
              value={filterBillable === undefined ? 'all' : filterBillable ? 'yes' : 'no'}
              onValueChange={(value) => {
                if (value === 'all') setFilterBillable(undefined);
                else setFilterBillable(value === 'yes');
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Billable Only</SelectItem>
                <SelectItem value="no">Non-Billable Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingState message="Updating time entries..." />
      ) : timeEntries.length === 0 ? (
        <EmptyState
          icon={Clock}
          title="No time entries found"
          description="Start tracking your time or create a manual entry"
          action={{
            label: 'Add Entry',
            onClick: () => {
              setEditingEntry(null);
              setFormOpen(true);
            },
          }}
        />
      ) : (
        <div className="space-y-4">
          {timeEntries.map((entry) => (
            <TimeEntryCard
              key={entry.id}
              entry={entry}
              assigneeLabel={userIdToLabel[entry.userId] || entry.userId}
              onEdit={() => handleEdit(entry)}
              onDelete={() => handleDelete(entry)}
              onReject={() => handleReject(entry)}
            />
          ))}
        </div>
      )}

      <TimeEntryForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingEntry(null);
        }}
        onSubmit={handleSubmit}
        initialData={editingEntry || undefined}
      />

      <TimeEntryImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        clients={clients}
        existingEntries={existingEntriesForImport}
        createTimeEntry={createTimeEntry}
        updateTimeEntry={updateTimeEntry}
        allowedUserIds={allowedImportUserIds}
      />
    </div>
  );
};
